import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Loader2, Printer, ArrowLeft, CheckCircle2, XCircle, Copy } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { BRAND } from "../../data/content";
import { QUOTE_TERMS } from "../../data/quoteTerms";

// Renders a printable, OEC-branded quote document.
// Supports two modes:
//   • Saved quote — fetches by id and shows status controls
//   • Preview — receives ?preview=<encoded-json> from the quote builder
export default function QuoteView() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const previewJson = params.get("preview");

  const [doc, setDoc] = useState(null);
  const [enquiry, setEnquiry] = useState(null);

  useEffect(() => {
    if (previewJson) {
      try {
        const decoded = JSON.parse(decodeURIComponent(previewJson));
        setDoc(decoded.quote);
        setEnquiry(decoded.enquiry);
      } catch { setDoc(null); }
      return;
    }
    api.get(`/quotes/${id}`).then(async (r) => {
      setDoc(r.data);
      try {
        const enqList = await api.get("/enquiries");
        setEnquiry(enqList.data.find((e) => e.id === r.data.enquiry_id));
      } catch {}
    }).catch(() => setDoc(null));
  }, [id, previewJson]);

  if (!doc) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  const isPreview = !!previewJson;
  const acceptanceLink = doc.acceptance_token ? `${window.location.origin}/q/${doc.acceptance_token}` : null;

  const copyLink = () => {
    if (!acceptanceLink) return;
    navigator.clipboard?.writeText(acceptanceLink);
    toast.success("Acceptance link copied — paste it into the client email");
  };

  const setStatus = async (status) => {
    try {
      await api.patch(`/quotes/${id}`, { status });
      toast.success(`Marked ${status}`);
      const r = await api.get(`/quotes/${id}`);
      setDoc(r.data);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  return (
    <div className="bg-slate-100 min-h-screen pb-20" data-testid="page-quote-view">
      {/* Action bar (hidden on print) */}
      <div className="bg-white border-b border-slate-200 px-6 py-4 print:hidden sticky top-0 z-20">
        <div className="max-w-[900px] mx-auto flex items-center justify-between gap-4 flex-wrap">
          <Link to="/admin/enquiries" className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-2"><ArrowLeft className="w-4 h-4" /> Back</Link>
          <div className="flex items-center gap-2">
            {!isPreview && (
              <>
                <StatusPill status={doc.status} />
                {acceptanceLink && doc.status !== "accepted" && doc.status !== "rejected" && doc.status !== "expired" && (
                  <button onClick={copyLink} className="text-xs font-semibold border border-[#1FA59A] text-[#1FA59A] hover:bg-[#1FA59A] hover:text-white px-3 py-2 flex items-center gap-1 transition-colors" data-testid="qv-copy-link">
                    <Copy className="w-3.5 h-3.5" /> Copy acceptance link
                  </button>
                )}
                {doc.status !== "accepted" && <button onClick={() => setStatus("accepted")} className="text-xs font-semibold border border-emerald-600 text-emerald-700 hover:bg-emerald-50 px-3 py-2 flex items-center gap-1" data-testid="qv-accept"><CheckCircle2 className="w-4 h-4" /> Mark accepted</button>}
                {doc.status !== "rejected" && <button onClick={() => setStatus("rejected")} className="text-xs font-semibold border border-red-600 text-red-700 hover:bg-red-50 px-3 py-2 flex items-center gap-1"><XCircle className="w-4 h-4" /> Mark rejected</button>}
                {doc.status === "draft" && <button onClick={() => setStatus("sent")} className="text-xs font-semibold border border-slate-300 hover:border-slate-900 text-slate-700 px-3 py-2">Mark sent</button>}
              </>
            )}
            <button onClick={() => window.print()} className="gs-gradient text-white font-bold px-5 py-2 flex items-center gap-2 text-sm hover:opacity-90" data-testid="qv-print">
              <Printer className="w-4 h-4" /> Print / Save as PDF
            </button>
          </div>
        </div>
      </div>

      {/* Document */}
      <div className="max-w-[900px] mx-auto bg-white my-8 shadow print:my-0 print:shadow-none print:max-w-none">
        {/* Header band */}
        <div className="bg-[#0A0F1C] text-white p-10 print:p-8 flex items-start justify-between gap-6">
          <div className="flex items-start gap-5">
            <div className="bg-white p-3 rounded-sm">
              <img src={BRAND.logo} alt="OEC" className="h-16 w-auto" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#7DD3C7] mb-2">Quotation</p>
              <h1 className="font-display text-3xl font-bold leading-tight">{BRAND.legalName}</h1>
              <p className="text-sm text-white/70 mt-2 leading-relaxed">
                {BRAND.address.line1}<br/>
                {BRAND.address.line2}<br/>
                {BRAND.address.city}, {BRAND.address.region}, {BRAND.address.postcode}
              </p>
              <p className="text-sm text-white/70 mt-2">{BRAND.phone} · {BRAND.email}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2">Reference</p>
            <p className="font-display text-2xl font-bold">{doc.reference}</p>
            {doc.job_number && (
              <p className="mt-2 text-xs uppercase tracking-[0.2em] text-[#7DD3C7]">Job no. <span className="font-mono font-bold text-white">{doc.job_number}</span></p>
            )}
            <p className="text-sm text-white/70 mt-3"><strong>Date:</strong> {new Date(doc.created_at).toLocaleDateString("en-GB")}</p>
            <p className="text-sm text-white/70"><strong>Valid until:</strong> {doc.valid_until}</p>
          </div>
        </div>

        {/* Client + intro */}
        <div className="p-10 print:p-8 border-b border-slate-200">
          <div className="grid grid-cols-2 gap-8 mb-8">
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-3">Prepared for</p>
              <p className="font-display text-xl font-bold text-slate-900">{doc.client_name || enquiry?.name}</p>
              {(doc.client_company || enquiry?.company) && <p className="text-slate-700">{doc.client_company || enquiry?.company}</p>}
              <p className="text-sm text-slate-600 mt-1">{doc.client_email || enquiry?.email}</p>
              {(doc.client_phone || enquiry?.phone) && <p className="text-sm text-slate-600">{doc.client_phone || enquiry?.phone}</p>}
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-3">Service</p>
              <p className="font-semibold text-slate-900">{doc.service || enquiry?.service || "General"}</p>
              {enquiry?.created_at && <p className="text-sm text-slate-500 mt-1">Original enquiry received {new Date(enquiry.created_at).toLocaleDateString("en-GB")}</p>}
            </div>
          </div>

          {doc.scope && (
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-3">Introduction</p>
              <div className="text-slate-700 whitespace-pre-wrap leading-relaxed">{doc.scope}</div>
            </div>
          )}
        </div>

        {/* Service sections (new model) or legacy flat line items */}
        {(doc.services?.length > 0) ? (
          <div className="p-10 print:p-8 border-b border-slate-200 space-y-8">
            {doc.services.map((sec, sidx) => {
              const secSubtotal = (sec.line_items || []).reduce((s, li) => s + (parseFloat(li.qty) || 0) * (parseFloat(li.unit_price) || 0), 0);
              return (
                <div key={sidx} className="break-inside-avoid">
                  <div className="flex items-baseline justify-between border-b-2 border-slate-900 pb-2 mb-3">
                    <h3 className="font-display text-lg font-bold text-slate-900">{sec.service}</h3>
                    <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Service {sidx + 1} of {doc.services.length}</span>
                  </div>
                  {sec.scope && <p className="text-sm text-slate-700 leading-relaxed mb-4 whitespace-pre-wrap">{sec.scope}</p>}
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-slate-500 border-b border-slate-200">
                        <th className="py-2 font-semibold">Description</th>
                        <th className="py-2 font-semibold w-16 text-right">Qty</th>
                        <th className="py-2 font-semibold w-28 text-right">Unit price</th>
                        <th className="py-2 font-semibold w-28 text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(sec.line_items || []).map((li, i) => {
                        const qty = parseFloat(li.qty) || 0; const up = parseFloat(li.unit_price) || 0;
                        return (
                          <tr key={i} className="border-b border-slate-100">
                            <td className="py-2 align-top">{li.description}</td>
                            <td className="py-2 text-right">{qty}</td>
                            <td className="py-2 text-right">£{up.toFixed(2)}</td>
                            <td className="py-2 text-right font-semibold">£{(qty * up).toFixed(2)}</td>
                          </tr>
                        );
                      })}
                      <tr className="bg-slate-50">
                        <td colSpan={3} className="py-2 text-right text-xs font-semibold text-slate-600">Section subtotal</td>
                        <td className="py-2 text-right font-bold">£{secSubtotal.toFixed(2)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              );
            })}
            <div className="ml-auto w-80 text-sm space-y-1 pt-4 border-t border-slate-900">
              <div className="flex justify-between"><span className="text-slate-600">Total (excl. VAT)</span><span className="font-semibold">£{(doc.subtotal || 0).toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-slate-600">VAT @ 20%</span><span className="font-semibold">£{(doc.vat || 0).toFixed(2)}</span></div>
              <div className="flex justify-between border-t-2 border-slate-900 pt-2 mt-2 text-base"><span className="font-bold">Total inc. VAT</span><span className="font-bold gs-gradient-text text-xl">£{(doc.total || 0).toFixed(2)}</span></div>
            </div>
          </div>
        ) : (
          // Legacy flat line items (kept for older quotes)
          <div className="p-10 print:p-8 border-b border-slate-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-slate-900 text-left text-[10px] uppercase tracking-[0.2em] text-slate-500">
                  <th className="py-3 font-semibold">Description</th>
                  <th className="py-3 font-semibold w-20 text-right">Qty</th>
                  <th className="py-3 font-semibold w-32 text-right">Unit price</th>
                  <th className="py-3 font-semibold w-32 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {(doc.line_items || []).map((li, i) => {
                  const qty = parseFloat(li.qty) || 0; const up = parseFloat(li.unit_price) || 0;
                  return (
                    <tr key={i} className="border-b border-slate-100">
                      <td className="py-3 align-top">{li.description}</td>
                      <td className="py-3 text-right">{qty}</td>
                      <td className="py-3 text-right">£{up.toFixed(2)}</td>
                      <td className="py-3 text-right font-semibold">£{(qty * up).toFixed(2)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="mt-6 ml-auto w-72 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-slate-600">Subtotal</span><span className="font-semibold">£{(doc.subtotal || 0).toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-slate-600">VAT @ 20%</span><span className="font-semibold">£{(doc.vat || 0).toFixed(2)}</span></div>
              <div className="flex justify-between border-t-2 border-slate-900 pt-2 mt-2 text-base"><span className="font-bold">Total</span><span className="font-bold gs-gradient-text">£{(doc.total || 0).toFixed(2)}</span></div>
            </div>
          </div>
        )}

        {/* Acceptance evidence (when client signed via /q/{token}) */}
        {doc.acceptance && (
          <div className="p-10 print:p-8 bg-emerald-50 border-b border-slate-200" data-testid="acceptance-evidence">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-6 h-6 text-emerald-700 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-display text-lg font-bold text-emerald-900">Quote accepted</p>
                <p className="text-emerald-800 mt-1">Signed by <strong>{doc.acceptance.signature_name}</strong>{doc.acceptance.signature_role ? `, ${doc.acceptance.signature_role}` : ""}</p>
                <p className="text-xs text-emerald-700 mt-1">{new Date(doc.acceptance.accepted_at).toLocaleString("en-GB")} · IP {doc.acceptance.accepted_ip}</p>
                <p className="text-xs text-emerald-700 mt-0.5">Terms &amp; Conditions acknowledged: {doc.acceptance.terms_acknowledged ? "Yes" : "No"}</p>
              </div>
            </div>
          </div>
        )}

        {/* Rejection evidence (when client declined via /q/{token}) */}
        {doc.rejection && (
          <div className="p-10 print:p-8 bg-amber-50 border-b border-slate-200" data-testid="rejection-evidence">
            <div className="flex items-start gap-3">
              <XCircle className="w-6 h-6 text-amber-700 flex-shrink-0 mt-0.5" />
              <div className="text-sm flex-1">
                <p className="font-display text-lg font-bold text-amber-900">Revision requested by client</p>
                <p className="text-amber-800 mt-1">Submitted by <strong>{doc.rejection.signature_name}</strong>{doc.rejection.signature_role ? `, ${doc.rejection.signature_role}` : ""}</p>
                <p className="text-xs text-amber-700 mt-1">{new Date(doc.rejection.rejected_at).toLocaleString("en-GB")} · IP {doc.rejection.rejected_ip}</p>
                <div className="mt-3 p-3 bg-white border border-amber-200 text-amber-900 whitespace-pre-wrap"><strong>Reason given:</strong> {doc.rejection.reason}</div>
              </div>
            </div>
          </div>
        )}

        {/* How to accept */}
        <div className="p-10 print:p-8 bg-slate-50 border-b border-slate-200">
          <p className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-3">To accept this quote</p>
          {acceptanceLink && !doc.acceptance ? (
            <>
              <p className="text-sm text-slate-700 leading-relaxed">Accept this quote with one click using the secure signed link below. By accepting, you confirm acceptance of our Terms &amp; Conditions of service. We capture your IP address and timestamp as evidence for our ISO-compliant audit trail.</p>
              <p className="mt-4 font-mono text-xs break-all bg-white border border-slate-200 p-3 print:hidden">{acceptanceLink}</p>
              <p className="mt-3 text-sm text-slate-700 print:hidden">Or reply to <a className="text-[#1FA59A] underline" href={`mailto:${BRAND.email}`}>{BRAND.email}</a> with your written acceptance.</p>
            </>
          ) : (
            <p className="text-sm text-slate-700 leading-relaxed">Please reply to <a className="text-[#1FA59A] underline" href={`mailto:${BRAND.email}`}>{BRAND.email}</a> confirming acceptance of this quotation and these Terms and Conditions. Works will be scheduled following written acceptance and any required pre-start information.</p>
          )}
        </div>

        {/* Terms */}
        <div className="p-10 print:p-8 text-xs text-slate-700 leading-relaxed whitespace-pre-wrap">
          {QUOTE_TERMS}
        </div>

        {/* Footer */}
        <div className="bg-[#0A0F1C] text-white/70 text-[10px] tracking-wider p-6 print:p-4 text-center uppercase">
          {BRAND.legalName} · {BRAND.address.line1}, {BRAND.address.city} {BRAND.address.postcode} · {BRAND.phone} · {BRAND.email}
        </div>
      </div>

      <style>{`@media print {
        body { background: white !important; }
        .print\\:hidden { display: none !important; }
      }`}</style>
    </div>
  );
}

function StatusPill({ status }) {
  const m = { draft: "bg-slate-100 text-slate-700", sent: "bg-blue-50 text-blue-700", accepted: "bg-emerald-50 text-emerald-700", rejected: "bg-red-50 text-red-700", expired: "bg-amber-50 text-amber-700" };
  return <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 ${m[status] || "bg-slate-100"}`}>{status}</span>;
}
